// Add deal's words and sums, kept apart from the page so each is tested without a DOM (addDeal.test.ts). The page is
// AddDealPage.tsx (module 10 U285 to U289, U293, U295, U297; ADD-DEAL-BRIEF-2026-10-05.md section 4; the Deal work
// lane's W6). The server checks everything again (apps/api/src/deals/work/add-deal.ts): these only say it first.
import { dayMonth, dayMonthYear } from '../day.js';
import type { AddressCheck, DealsAdded, PayBasis, PayBody, Period } from './api.js';

/** crm.product_families()'s catch-all, as W2 names it. */
export const OTHER = 'Other';

/** The pay of a year from an amount and its period. */
const yearly = (n: number, p: Period | null | undefined): number => (p === 'month' ? n * 12 : n);

/**
 * What we expect to earn in a year (U295), as the server works it out (add-deal.ts expectedYear, the same rule): the
 * figure typed, else a fixed fee made a year; the share of their base, never below the minimum; the rate a match times
 * the matches a year, never below the minimum; else the minimum. Null when the terms give no figure.
 */
export function expectedYear(p: PayBody | null): number | null {
  if (!p) return null;
  if (p.expected !== null && p.expected !== undefined) return p.expected;
  const min = p.minimum ?? null;
  const floor = min !== null ? yearly(min, p.minimumPeriod) : 0;
  let worked: number | null = null;
  if (p.basis === 'fixed' && p.fee != null) worked = yearly(p.fee, p.feePeriod);
  if (p.basis === 'share' && p.sharePct != null && p.base != null) worked = (p.sharePct / 100) * yearly(p.base, p.basePeriod);
  if (p.basis === 'per_match' && p.matchRate != null && p.matchCount != null) worked = p.matchRate * p.matchCount;
  if (worked === null) return min !== null ? floor : null;
  return Math.round(Math.max(worked, floor) * 100) / 100;
}

/**
 * An amount as a person types it ("120,000", "1 200", "15%"), or null when the box is empty; NaN when it is not a number.
 * A share is typed with its sign as often as without: "15%" refused Mike's Add on 7 Oct 2026 (B2251).
 */
export function amountOf(typed: string): number | null {
  const t = typed.replace(/[,\s£€$%]/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
}

/**
 * One product's Expected revenues as the person types them: each box's text and the choices beside it. Each product of
 * an Add has its own (Mike, 7 Oct 2026: "you have 3 deal types and one 'How it pays' when there needs to be one per
 * deal"; B2251).
 */
export interface PayForm {
  basis: PayBasis | null; currency: string;
  fee: string; feePeriod: Period;
  sharePct: string; shareOf: string;
  minimum: string; minimumPeriod: Period;
  /**
   * On a revenue share, our potential share above the minimum, in money: Mike, 7 Oct 2026, "The MG is the MG, when we
   * quote an estimate of the rev share, this means the amount over and above the mg", and "your table was suppose to
   * total the MRG and the potential NGR share" (B2282). It replaces "Their expected NGR".
   */
  above: string; abovePeriod: Period;
  matchRate: string; matchCount: string;
  expected: string;
}
export const BLANK_PAY: PayForm = {
  basis: null, currency: 'GBP', fee: '', feePeriod: 'year', sharePct: '', shareOf: 'ngr', minimum: '', minimumPeriod: 'year',
  above: '', abovePeriod: 'year', matchRate: '', matchCount: '', expected: '',
};

export type PayBox = 'fee' | 'sharePct' | 'minimum' | 'above' | 'matchRate' | 'matchCount' | 'expected';
/**
 * The boxes each basis draws, and so the only ones read: a box left filled under another basis is not shown and is never
 * a reason to refuse the Add (Mike's Add of 7 Oct 2026 was refused for a box he could not see; B2251).
 */
export const PAY_BOXES: Record<PayBasis, readonly PayBox[]> = {
  fixed: ['fee', 'expected'],
  share: ['sharePct', 'minimum', 'above', 'expected'],
  per_match: ['matchRate', 'matchCount', 'minimum', 'expected'],
  unknown: [],
};

/** A box's name as the page draws it over the box. */
export function boxWords(b: PayBox, shareOf: string): string {
  switch (b) {
    case 'fee': return 'Fee';
    case 'sharePct': return 'Share';
    case 'minimum': return 'Minimum guarantee';
    case 'above': return `Potential ${SHARE_OF_WORDS[shareOf] ?? 'revenue'} share`;
    case 'matchRate': return 'Rate a match';
    case 'matchCount': return 'Matches a year';
    default: return 'What we expect to earn this year';
  }
}

/**
 * What the page sends for one product's Expected revenues, read from the boxes its basis draws and no other; or the
 * first of those boxes whose text is not a number. No basis chosen sends nothing.
 */
export function payOf(f: PayForm): { pay: PayBody | null } | { bad: PayBox } {
  if (!f.basis) return { pay: null };
  if (f.basis === 'unknown') return { pay: { basis: 'unknown', currency: f.currency } };
  const v: Partial<Record<PayBox, number | null>> = {};
  for (const b of PAY_BOXES[f.basis]) {
    const n = amountOf(f[b]);
    if (Number.isNaN(n)) return { bad: b };
    v[b] = n;
  }
  const p: PayBody = { basis: f.basis, currency: f.currency, expected: v.expected ?? null };
  if (f.basis === 'fixed') Object.assign(p, { fee: v.fee ?? null, feePeriod: f.feePeriod });
  // A revenue share's year is the minimum plus our potential share above it (B2282): sent as the figure, since the
  // server's own rule works from their base, which this form no longer asks for.
  if (f.basis === 'share') Object.assign(p, { sharePct: v.sharePct ?? null, shareOf: f.shareOf, expected: v.expected ?? workedYear(f) });
  if (f.basis === 'per_match') Object.assign(p, { matchRate: v.matchRate ?? null, matchCount: v.matchCount ?? null });
  if (f.basis !== 'fixed' && v.minimum != null) Object.assign(p, { minimum: v.minimum, minimumPeriod: f.minimumPeriod });
  return { pay: p };
}

const r2 = (x: number): number => Math.round(x * 100) / 100;
const numberIn = (s: string): number | null => { const x = amountOf(s); return x === null || Number.isNaN(x) ? null : x; };

/**
 * The year the terms give, before any figure is typed over it: a fixed fee made a year; on a revenue share the minimum
 * plus our potential share above it (B2282, Mike's "total the MRG and the potential NGR share"), or either alone; per
 * match the rate times the matches a year, never below the minimum. Null when the boxes give no figure.
 */
export function workedYear(f: PayForm): number | null {
  const m = numberIn(f.minimum);
  const min = m !== null ? yearly(m, f.minimumPeriod) : null;
  if (f.basis === 'fixed') { const fee = numberIn(f.fee); return fee === null ? null : r2(yearly(fee, f.feePeriod)); }
  if (f.basis === 'share') {
    const a = numberIn(f.above);
    if (a === null && min === null) return null;
    return r2((min ?? 0) + (a !== null ? yearly(a, f.abovePeriod) : 0));
  }
  if (f.basis === 'per_match') {
    const rate = numberIn(f.matchRate); const count = numberIn(f.matchCount);
    if (rate === null || count === null) return min;
    return r2(Math.max(rate * count, min ?? 0));
  }
  return null;
}

export const CURRENCY_SIGN: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };
/** "£180,000": a whole amount in its currency, as the card writes money. */
export function money(n: number, currency: string): string {
  const s = Math.round(n).toLocaleString('en-GB');
  const sign = CURRENCY_SIGN[currency];
  return sign ? `${sign}${s}` : `${s} ${currency}`;
}

export const BASIS_WORDS: Record<PayBasis, string> = { fixed: 'Fixed fee', share: 'Revenue share', per_match: 'Per match', unknown: 'Not known yet' };
export const SHARE_OF_WORDS: Record<string, string> = { ngr: 'NGR', ggr: 'GGR', turnover: 'Turnover', net_revenue: 'Net revenue' };

/** "We expect £180,000 a year, of which £120,000 is the minimum." Null when the terms give no figure. */
export function expectWords(p: PayBody | null): string | null {
  const y = expectedYear(p);
  if (y === null || !p) return null;
  const min = p.minimum != null ? yearly(p.minimum, p.minimumPeriod) : null;
  return `We expect ${money(y, p.currency)} a year${min !== null && min > 0 ? `, of which ${money(Math.min(min, y), p.currency)} is the minimum` : ''}.`;
}

/** Whose a deal is, in the foot's words: "yours", or "Will Ash's". */
export const ownerWord = (ownerId: string, me: string | null, name: string | null): string => (ownerId === me ? 'yours' : `${name ?? 'a colleague'}'s`);

const list = (xs: readonly string[]): string => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const conversations = (n: number): string => `${n} conversation${n === 1 ? '' : 's'}`;

export interface Summary {
  company: string;
  /** One per deal: its product (or the property, on the rights side), stage name and owner word. */
  deals: Array<{ product: string; stage: string; owner: string }>;
  ticked: number;
}

/** The foot's line of what will be made: "Kaizen, One Retail, at Talking, yours, with 1 conversation filed under it." */
export function summaryWords(s: Summary): string {
  if (!s.company || s.deals.length === 0) return '';
  if (s.deals.length === 1) {
    const d = s.deals[0]!;
    return `${s.company}, ${d.product}, at ${d.stage}, ${d.owner}${s.ticked > 0 ? `, with ${conversations(s.ticked)} filed under it` : ''}.`;
  }
  const owners = new Set(s.deals.map((d) => d.owner));
  const whose = owners.size === 1 ? `, all ${[...owners][0]}` : '';
  return `${s.company}, ${s.deals.length} deals: ${list(s.deals.map((d) => d.product))}${whose}${s.ticked > 0 ? `, with ${conversations(s.ticked)} filed under each` : ''}.`;
}

/** The yellow lead's words (the brief: "Add this deal", "Add the company and 3 deals"). */
export function leadWords(n: number, newCompany: boolean): string {
  const deals = n > 1 ? `${n} deals` : 'this deal';
  return newCompany ? `Add the company and ${deals}` : n > 1 ? `Add ${deals}` : 'Add this deal';
}

/**
 * After Add: "Added: Kaizen, One Retail, at Talking, yours. 1 conversation filed under it." `ticked` is the conversations
 * ticked in the column, as the foot counted them (the server's `filed` counts their messages).
 */
export function addedWords(a: DealsAdded, stageName: (code: string) => string, ticked: number): string {
  const conv = ticked > 0 ? ` ${conversations(ticked)} filed under ${a.deals.length > 1 ? 'each' : 'it'}.` : '';
  if (a.deals.length === 1) {
    const d = a.deals[0]!;
    return `Added: ${d.title}, at ${stageName(d.stage)}, ${d.ownerMine ? 'yours' : `${d.owner}'s`}.${conv}`;
  }
  return `Added ${a.newCompany ? `${a.organisationName} and ` : ''}${a.deals.length} deals: ${list(a.deals.map((d) => d.product ?? d.title))}.${conv}`;
}

/** What a new company's address says as it is typed (U288). `use` is the company the line offers instead, when there is one. */
export function addressWords(c: AddressCheck, name: string, brand: string): { text: string; use: { id: string; name: string } | null; blocks: boolean } {
  if (c.owner) return { text: `${c.domain} is ${c.owner.name}'s. A second company would split its mail in two.`, use: c.owner, blocks: true };
  if (c.named) return { text: `${c.named.name} is already on record.`, use: c.named, blocks: true };
  const called = [name.trim(), brand.trim()].filter(Boolean);
  const none = called.length > 0 ? `No company called ${called.join(' or ')}.` : '';
  if (c.free) return { text: `${none} ${c.domain} is free mail, so it is not taken as the company's domain; the address goes on the record as a person.`.trim(), use: null, blocks: false };
  if (!c.domain) return { text: none, use: null, blocks: false };
  const held = c.held > 0
    ? `Cobrain already holds ${c.held} ${c.held === 1 ? 'message' : 'messages'} with the address's domain; ${c.held === 1 ? 'it goes' : 'they go'} on the new record.`
    : `Cobrain holds no messages with ${c.domain} yet.`;
  return { text: `${none} ${held}`.trim(), use: null, blocks: false };
}

/** "From the 1 Oct meeting": where Cobrain read the step it suggests. */
export function fromWords(at: string, kind: string): string {
  const d = new Date(at);
  const day = Number.isNaN(d.getTime()) ? '' : dayMonth(d);
  const what = kind === 'meeting' || kind === 'calendar_event' ? 'meeting' : kind === 'email' ? 'mail' : kind === 'call' ? 'call' : 'conversation';
  return `From the ${day} ${what}`.replace(/\s+/g, ' ');
}

/** "14 Nov 2025": a conversation's date in the column. */
export function dayWords(at: string | null): string {
  if (!at) return '';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : dayMonthYear(d);
}

/**
 * The add just made, carried from the page to Deals (the page goes back to Deals, opened on the new line, and says what
 * it added there with its Undo, a week, D62). Held in the module for the session's page changes; a reload drops the line
 * and never the deal.
 */
export interface AddedNote { store: string; added: DealsAdded; words: string }
let lastAdded: AddedNote | null = null;
export const noteAdded = (n: AddedNote | null): void => { lastAdded = n; };
export const addedNote = (store: string): AddedNote | null => (lastAdded && lastAdded.store === store ? lastAdded : null);
